import React, { useState, useCallback, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { TypeOf, z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter } from "next/router";
import axios from "axios";
import { createPaymentOrder } from "@/marketingUtils/lpPayment";
import { loginEvent, trackEvent } from "@/lib/analytics";
import { FortuneReportEventName } from "@/lib/analytics/events/fortuneReport";
import { getCookie } from "cookies-next";

const NOMINATIM_API_URL = "https://nominatim.openstreetmap.org/search";

const userDataSchema = z.object({
  name: z.string().min(1, { message: "Name is required" }),
  email: z.string().email({ message: "Invalid Email Address" }),
  phone: z
    .string()
    .min(10, { message: "Phone number should be 10 digits" })
    .max(10, { message: "Phone number should be 10 digits" })
    .refine((value) => /^\d+$/.test(value), {
      message: "Phone number should contain only numbers",
    })
    .refine((value) => /^(6|7|8|9)\d{9}$/.test(value), {
      message: "Phone number should be a valid Indian number",
    }),
  dob: z.string().min(5, { message: "DOB Is Needed" }),
  time: z
    .string()
    .min(2, { message: "Time of Birth should be in 24-hour format" }),
  placeofbirth: z.object({
    place: z.string(),
    latitude: z.number(),
    longitude: z.number(),
    timezone_id: z.string().optional(),
    timezone: z.number().optional(),
  }),
});

/* ─── Reusable styled input ─── */
function StyledInput({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-[11px] uppercase tracking-[0.06em] font-medium text-fr-muted font-inter">
        {label} <span className="text-fr-gold">*</span>
      </label>
      {children}
      {error && (
        <p className="text-fr-error text-xs flex items-center gap-1 font-inter">
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
          {error}
        </p>
      )}
    </div>
  );
}

/* ─── Success animation ─── */
function SuccessState() {
  return (
    <div className="flex flex-col items-center justify-center gap-5 py-8">
      <div className="relative">
        <svg
          width="80"
          height="80"
          viewBox="0 0 80 80"
          className="drop-shadow-[0_0_20px_rgba(232,185,74,0.3)]"
        >
          <circle
            cx="40"
            cy="40"
            r="36"
            fill="none"
            stroke="#E8B94A"
            strokeWidth="3"
            strokeDasharray="226"
            strokeDashoffset="226"
            className="animate-fr-circle-draw"
            strokeLinecap="round"
          />
          <path
            d="M24 40 L35 51 L56 30"
            fill="none"
            stroke="#E8B94A"
            strokeWidth="3"
            strokeDasharray="40"
            strokeDashoffset="40"
            className="animate-fr-check-draw"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <h2 className="font-serif text-2xl text-fr-gold tracking-tight">
        Payment Successful
      </h2>
      <p className="text-fr-muted text-sm font-inter">
        Preparing your Fortune Report
        <span className="inline-flex ml-1">
          <span className="animate-bounce [animation-delay:0ms]">.</span>
          <span className="animate-bounce [animation-delay:150ms]">.</span>
          <span className="animate-bounce [animation-delay:300ms]">.</span>
        </span>
      </p>
    </div>
  );
}

/* ─── Spinner for loading states ─── */
function GoldSpinner({ size = 20 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className="animate-spin"
    >
      <circle
        cx="12"
        cy="12"
        r="10"
        fill="none"
        stroke="rgba(232,185,74,0.2)"
        strokeWidth="3"
      />
      <path
        d="M12 2 A10 10 0 0 1 22 12"
        fill="none"
        stroke="#E8B94A"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}

/* ─── Main component ─── */
export default function FortuneReportLead({
  masterclassData,
}: {
  masterclassData: any;
}) {
  const [loading, setLoading] = useState(false);
  const [isInfoProcessing, setIsInfoProcessing] = useState(false);
  const [locations, setLocations] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  const router = useRouter();
  const { source, comment } = router.query;
  const slotInfo = masterclassData?.slots?.find(
    (slot: any) => slot.active === true,
  );
  const id = masterclassData?.id || "cm0tamxwa5k1kpc2jz5woru7z";
  const redirectLink = `https://astrolearn.co/${id}/e-redirect/thanks-paid`;

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<z.infer<typeof userDataSchema>>({
    resolver: zodResolver(userDataSchema),
  });

  const fetchLocations = useCallback(async (query: string) => {
    setIsSearching(true);
    try {
      const response = await axios.get(NOMINATIM_API_URL, {
        params: {
          q: query,
          format: "json",
          limit: 10,
        },
        headers: {
          "User-Agent": "AstroLearn/1.0",
        },
      });
      setLocations(response.data || []);
    } catch (err) {
      console.error("Error fetching locations:", err);
    } finally {
      setIsSearching(false);
    }
  }, []);

  const debouncedFetchLocations = useMemo(() => {
    let timeoutId: NodeJS.Timeout;
    return (query: string) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        if (query.length > 2) {
          fetchLocations(query);
        }
      }, 300);
    };
  }, [fetchLocations]);

  const submitForm = async (data: TypeOf<typeof userDataSchema>) => {
    setLoading(true);
    trackEvent(FortuneReportEventName.FORTUNE_REPORT_DETAILS_COMPLETED, {
      dateOfBirth: new Date(data.dob),
      timeOfBirth: new Date(data.time),
      placeOfBirth: data.placeofbirth.place,
    });
    const { name, email, phone, dob, time, placeofbirth } = data;

    loginEvent({
      name,
      email,
      phone,
    });

    const fbclid = getCookie("_fbc") ?? router.query.fbclid ?? undefined;
    const fbpid = getCookie("_fbp") ?? undefined;
    const leadReffererMetadata = {
      ...(fbclid && { fbclid }),
      ...(fbpid && { fbpid }),
      ...(router.query.gclid && { gclid: router.query.gclid }),
    };

    const masterclassLeadPayload = {
      name,
      email,
      phone,
      slotId: slotInfo?.id,
      source: (source as string) || "direct",
      origin: window.location.href,
      comment: (comment as string) || "fortune_report",
      payload: {
        dob,
        time,
        placeofbirth: placeofbirth.place,
        latitude: placeofbirth.latitude,
        longitude: placeofbirth.longitude,
      },
      reffererMetadata: leadReffererMetadata,
    };

    const leadPayload = {
      name,
      email,
      phone,
      source: (source as string) || "direct",
      comment: (comment as string) || "fortune_report",
      masterclassSlotId: slotInfo?.id,
      masterclassId: masterclassData?.id,
      placeofbirth: placeofbirth.place,
      latitude: placeofbirth.latitude,
      longitude: placeofbirth.longitude,
      dob,
      time,
      reffererMetadata: leadReffererMetadata,
    };

    try {
      const { data: leadData } = await axios.post(
        `${process.env.NEXT_PUBLIC_LEADS_API}leads`,
        masterclassLeadPayload,
      );

      await createPaymentOrder({
        leadPayload,
        masterClassData: masterclassData,
        paymentMethod: source === "google" ? "paidMasterClass" : "paidBootcamp",
        paymentName: "Astrolearn Fortune Report",
        paymentToken:
          "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJhbW91bnQiOjk5LCJpYXQiOjE3NDQ0NDAzMDR9.6yfD5szTTP3y2FkPZAOcXbRaU3JUxNiNWdkhu0mX5zk",
        onSuccess: async () => {
          setIsInfoProcessing(true);
          try {
            const formData = new FormData();
            formData.append("name", name);
            formData.append("email", email);
            formData.append("mobile", phone);
            formData.append("source", (source as string) || "default_source");
            formData.append("comment", "fortune_report");

            window.location.href = redirectLink;
          } catch (error) {
            console.error("Error in lead/Google Sheets submission:", error);
          }
        },
      });
    } catch (error) {
      console.error("Error during payment:", error);
    } finally {
      setLoading(false);
    }
  };

  const inputBaseClass =
    "w-full bg-fr-surface !border !border-white/[0.08] !rounded-xl px-4 py-3 !text-fr-text text-sm font-inter placeholder:text-fr-muted/60 outline-none transition-all duration-200 focus:!border-fr-gold focus:!ring-[3px] focus:!ring-fr-gold/10";

  return (
    <div className="bg-fr-navy rounded-2xl">
      {isInfoProcessing ? (
        <SuccessState />
      ) : (
        <div className="flex flex-col gap-6">
          {/* Header */}
          <div className="text-center">
            {/* Small mandala accent */}
            <div className="flex justify-center mb-3">
              <svg
                width="32"
                height="32"
                viewBox="0 0 32 32"
                className="text-fr-gold opacity-60"
              >
                <circle
                  cx="16"
                  cy="16"
                  r="14"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="0.5"
                />
                <circle
                  cx="16"
                  cy="16"
                  r="10"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="0.5"
                />
                <circle
                  cx="16"
                  cy="16"
                  r="6"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="0.5"
                />
                <circle
                  cx="16"
                  cy="16"
                  r="2"
                  fill="currentColor"
                  opacity="0.4"
                />
                {[0, 45, 90, 135, 180, 225, 270, 315].map((angle) => (
                  <line
                    key={angle}
                    x1="16"
                    y1="2"
                    x2="16"
                    y2="30"
                    stroke="currentColor"
                    strokeWidth="0.3"
                    opacity="0.3"
                    transform={`rotate(${angle} 16 16)`}
                  />
                ))}
              </svg>
            </div>
            <h1 className="font-serif text-2xl md:text-3xl bg-gradient-to-r from-fr-gold via-yellow-300 to-fr-gold bg-clip-text text-transparent">
              Get Your Fortune Report
            </h1>
            <div className="mx-auto mt-2 w-16 h-px bg-gradient-to-r from-transparent via-fr-gold/40 to-transparent" />
          </div>

          {/* Form */}
          <form
            onSubmit={handleSubmit(submitForm)}
            onClick={() => {
              trackEvent(
                FortuneReportEventName.FORTUNE_REPORT_DETAILS_INITIATED,
              );
            }}
            className="flex flex-col gap-5"
          >
            {/* Name */}
            <StyledInput label="Full Name" error={errors.name?.message}>
              <input
                type="text"
                placeholder="Enter your full name"
                className={`${inputBaseClass} ${errors.name ? "!border-fr-error" : ""}`}
                {...register("name")}
              />
            </StyledInput>

            {/* Email */}
            <StyledInput label="Email Address" error={errors.email?.message}>
              <input
                type="email"
                placeholder="you@example.com"
                className={`${inputBaseClass} ${errors.email ? "!border-fr-error" : ""}`}
                {...register("email")}
              />
            </StyledInput>

            {/* Phone */}
            <StyledInput label="Phone Number" error={errors.phone?.message}>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-fr-muted text-sm font-inter">
                  +91
                </span>
                <input
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  placeholder="10-digit mobile number"
                  className={`${inputBaseClass} !pl-12 ${errors.phone ? "!border-fr-error" : ""}`}
                  {...register("phone")}
                />
              </div>
            </StyledInput>

            {/* DOB & Time — side by side */}
            <div className="grid grid-cols-2 gap-3">
              <StyledInput label="Date of Birth" error={errors.dob?.message}>
                <input
                  type="date"
                  className={`${inputBaseClass} ${errors.dob ? "!border-fr-error" : ""}`}
                  {...register("dob")}
                />
              </StyledInput>

              <StyledInput label="Time of Birth" error={errors.time?.message}>
                <input
                  type="time"
                  className={`${inputBaseClass} ${errors.time ? "!border-fr-error" : ""}`}
                  {...register("time")}
                />
              </StyledInput>
            </div>

            {/* Place of Birth */}
            <Controller
              control={control}
              name="placeofbirth"
              render={({ field: { onChange, value } }) => (
                <StyledInput
                  label="Place of Birth"
                  error={errors.placeofbirth?.message}
                >
                  <div className="relative">
                    {/* Search icon */}
                    <svg
                      className="absolute left-3.5 top-1/2 -translate-y-1/2 text-fr-muted/60 pointer-events-none"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="11" cy="11" r="8" />
                      <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>

                    {/* Searching spinner */}
                    {isSearching && (
                      <div className="absolute right-3.5 top-1/2 -translate-y-1/2">
                        <GoldSpinner size={16} />
                      </div>
                    )}

                    <input
                      type="text"
                      className={`${inputBaseClass} !pl-10 ${errors.placeofbirth ? "!border-fr-error" : ""}`}
                      placeholder="Search your birth city..."
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        debouncedFetchLocations(e.target.value);
                      }}
                    />

                    {/* Dropdown */}
                    {locations.length > 0 && searchQuery && (
                      <div
                        className="absolute z-20 w-full mt-1.5 bg-fr-surface border border-white/[0.08] rounded-xl overflow-hidden shadow-2xl shadow-black/40 max-h-52 overflow-y-auto"
                        role="listbox"
                      >
                        {locations.map((location: any) => (
                          <div
                            key={location.place_id}
                            role="option"
                            aria-selected={false}
                            className="px-4 py-3 text-sm text-fr-text/80 font-inter cursor-pointer
                                       hover:bg-fr-gold/10 hover:text-fr-text
                                       border-l-2 border-transparent hover:border-fr-gold
                                       transition-all duration-150"
                            onClick={() => {
                              onChange({
                                place: location.display_name,
                                latitude: parseFloat(location.lat),
                                longitude: parseFloat(location.lon),
                              });
                              setSearchQuery(location.display_name);
                              setLocations([]);
                            }}
                          >
                            <span className="line-clamp-2">
                              {location.display_name}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </StyledInput>
              )}
            />

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full mt-1 py-3.5 rounded-xl font-inter font-bold text-base
                         bg-gradient-to-r from-fr-gold to-[#D4A03C] !text-fr-dark
                         hover:from-[#D4A03C] hover:to-fr-gold
                         shadow-[0_0_30px_rgba(232,185,74,0.2)]
                         hover:shadow-[0_0_40px_rgba(232,185,74,0.35)]
                         transform hover:scale-[1.02] active:scale-[0.98]
                         transition-all duration-300 ease-out
                         disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <GoldSpinner size={18} />
                  Processing...
                </span>
              ) : (
                "Get Your Fortune Report"
              )}
            </button>

            {/* Security note */}
            <p className="text-center text-[11px] text-fr-muted/50 font-inter flex items-center justify-center gap-1">
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              Secure payment &middot; Your data is encrypted
            </p>
          </form>
        </div>
      )}
    </div>
  );
}
